// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN AUTH GUARD — Secure Access Control for Admin Dashboard
// EnergiPro Solutions — Company Email Restricted Access
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  Shield, Lock, Mail, Loader2, ArrowRight, LogIn, AlertCircle, CheckCircle2, X
} from 'lucide-react';
import {
  getStoredSession, requestOTP, verifyOTP, logout,
  startInactivityMonitor, stopInactivityMonitor, verifySession,
  AuthSession
} from '../../store/authStore';

type LoginStep = 'email' | 'otp';

interface AdminAuthGuardProps {
  children: React.ReactNode;
}

export const AdminAuthGuard: React.FC<AdminAuthGuardProps> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const stored = getStoredSession();
      if (stored && stored.role === 'admin') {
        const { valid } = await verifySession(stored.token);
        if (valid) {
          setSession(stored);
        }
      }
      setIsChecking(false);
    };
    checkSession();
  }, []);

  // Inactivity monitor
  useEffect(() => {
    if (session) {
      startInactivityMonitor(() => {
        setSession(null);
      });
      return () => stopInactivityMonitor();
    }
  }, [session]);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const emailLower = email.trim().toLowerCase();
    const domain = emailLower.split('@')[1];
    const allowedDomains = ['energiprogroup.com', 'energipro-org.com'];

    if (!allowedDomains.includes(domain)) {
      setError('Access restricted to authorized company emails only.');
      return;
    }

    setLoading(true);
    setError(null);

    const result = await requestOTP(emailLower, 'admin');

    if (result.success) {
      setStep('otp');
      setSuccessMessage('Verification code sent to your email.');
    } else {
      setError(result.error || 'Unable to send verification code.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) return;

    setLoading(true);
    setError(null);

    const result = await verifyOTP(email.trim().toLowerCase(), otp.trim());

    if (result.success && result.session) {
      setSession(result.session);
    } else {
      setError(result.error || 'Invalid verification code.');
    }
    setLoading(false);
  };

  // Loading state
  if (isChecking) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
        </div>
      </Layout>
    );
  }

  // Authenticated — render children
  if (session) {
    return <>{children}</>;
  }

  // Login UI
  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center py-16">
        <div className="w-full max-w-md px-4">
          <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-8">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase tracking-wide">Admin Access</h2>
              <p className="text-slate-400 text-sm mt-2">Restricted to authorized personnel</p>
            </div>

            {step === 'email' ? (
              <form onSubmit={handleRequestOTP} className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-1.5">Company Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="name@energiprogroup.com"
                      required
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <p className="text-slate-500 text-xs mt-2">Only @energiprogroup.com emails are accepted</p>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 text-red-300 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {loading ? 'Sending...' : 'Verify Identity'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                {successMessage && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2.5 text-green-300 text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {successMessage}
                  </div>
                )}

                <div>
                  <label className="block text-slate-400 text-sm mb-1.5">Verification Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    maxLength={6}
                    autoFocus
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white text-center text-2xl font-mono tracking-[0.5em] placeholder:text-slate-600 placeholder:tracking-[0.5em] focus:outline-none focus:border-orange-500 transition-colors"
                  />
                  <p className="text-slate-500 text-xs mt-2">Enter the 6-digit code sent to {email}</p>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 text-red-300 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || otp.length < 6}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                  {loading ? 'Verifying...' : 'Access Dashboard'}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('email'); setOtp(''); setError(null); setSuccessMessage(null); }}
                  className="w-full text-slate-500 text-xs hover:text-slate-300 transition-colors pt-2"
                >
                  ← Back to email
                </button>
              </form>
            )}

            <div className="mt-8 pt-6 border-t border-slate-700/50 text-center">
              <div className="flex items-center justify-center gap-2">
                <Shield className="w-3 h-3 text-red-500" />
                <span className="text-slate-600 text-[10px] uppercase tracking-wider">Role-based access control</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
